import {createContext, use, useEffect, useRef, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'

import {getDiffSizeCategory, type DiffSizeCategory} from '../helpers/get-diff-size-category'
import {useDiffstatRouterData} from '../page-data/loaders/use-diffstat-data'

export type PRAnalyticsInitialStateData = {
  diffVersion: 'v1' | 'v2'
  virtualizationEnabled: boolean
}
type State = Pick<PRAnalyticsInitialStateData, 'diffVersion'> & {
  diffSizeCategory: DiffSizeCategory
  virtualizationEnabled: 'true' | 'false'
  getINPDiffTags: () => {
    diffSizeCategory: DiffSizeCategory
    diffVersion: 'v1' | 'v2'
    virtualizationEnabled: 'true' | 'false'
  }
}
type PullRequestAnalyticsStore = ReturnType<typeof createPullRequestAnalyticsStore>

const PullRequestAnalyticsStoreContext = createContext<PullRequestAnalyticsStore | null>(null)

export function usePullRequestAnalyticsStore<T>(selector: (state: State) => T): T {
  const store = use(PullRequestAnalyticsStoreContext)
  if (!store) throw new Error('Missing PullRequestAnalyticsStoreProvider in the tree')
  return useStore(store, selector)
}

type PullRequestAnalyticsStoreProviderProps = {
  children?: ReactNode
  initData: PRAnalyticsInitialStateData
}
export const PullRequestAnalyticsStoreProvider = ({initData, children}: PullRequestAnalyticsStoreProviderProps) => {
  const storeRef = useRef<PullRequestAnalyticsStore | null>(null)
  const diffstat = useDiffstatRouterData()

  // eslint-disable-next-line react-hooks/refs
  if (!storeRef.current) {
    storeRef.current = createPullRequestAnalyticsStore(initData)
  }

  useEffect(() => {
    if (storeRef.current && diffstat) {
      storeRef.current.setState({diffSizeCategory: getDiffSizeCategory(diffstat.linesChanged)})
    }
  }, [diffstat])

  // We disable ESlint refs here as we need our Zustand stores to be a stable reference
  // Using a useState to store our Zustand store will cause unnecessary re-renders and downstream performance issues
  // eslint-disable-next-line react-hooks/refs
  return <PullRequestAnalyticsStoreContext value={storeRef.current}>{children}</PullRequestAnalyticsStoreContext>
}

const createPullRequestAnalyticsStore = (initStateData: PRAnalyticsInitialStateData) => {
  return createStore<State>()(
    devtools(
      (_, get) => ({
        ...initStateData,
        virtualizationEnabled: initStateData.virtualizationEnabled === true ? 'true' : 'false',
        diffSizeCategory: 'unknown',
        getINPDiffTags: () => ({
          diffVersion: get().diffVersion,
          diffSizeCategory: get().diffSizeCategory,
          virtualizationEnabled: get().virtualizationEnabled,
        }),
      }),
      {name: 'analytics-store'},
    ),
  )
}

try{ PullRequestAnalyticsStoreContext.displayName ||= 'PullRequestAnalyticsStoreContext' } catch {}
try{ PullRequestAnalyticsStoreProvider.displayName ||= 'PullRequestAnalyticsStoreProvider' } catch {}